import React, { useMemo, useCallback } from "react";
import qs from "query-string";
import dayjs from "dayjs";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import { useTranslation } from "react-i18next";
import useToggle from "react-use/lib/useToggle";

import { toast } from "react-toastify";
import { useMutation, useQuery } from "@apollo/client";

import {
  getClientFeatureQuery,
  QUERY_GOOGLE_CHAT,
} from "../../../../config/graphql/query";
import {
  MUTATION_DELETE_GOOGLE_CHAT,
  TOGGLE_CLIENT_FEATURE,
} from "../../../../config/graphql/mutation";

import { useClientId } from "../hooks/useClient";

import { getApiUrl } from "../../../../lib/common/getApiUrl";
import { ToggleFeatureButton } from "../Shelly/ShellyControllers";
import useHasRole from "../../../../lib/hooks/useHasRole";

interface IGoogleChat {
  id: string;
  googleAccountId: string;
  email: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

const GoogleChatIntegration = React.memo(() => {
  const [show, setShow] = useToggle(false);
  const { t } = useTranslation(["client", "common"]);

  const clientId = useClientId();

  const [onDelete] = useMutation(MUTATION_DELETE_GOOGLE_CHAT, {
    refetchQueries: [
      {
        query: QUERY_GOOGLE_CHAT,
      },
    ],
  });

  const { data: googleChatData, loading } = useQuery<{
    googleChat: IGoogleChat;
  }>(QUERY_GOOGLE_CHAT, {
    context: {
      headers: {
        ClientId: clientId,
      },
    },
    fetchPolicy: "network-only",
  });

  const hasGoogleChatIntegration = useMemo(
    () => !loading && !!googleChatData?.googleChat,
    [googleChatData, loading],
  );

  const googleChatId = useMemo(
    () => !loading && googleChatData?.googleChat?.id,
    [googleChatData, loading],
  );

  const onRemoveClientIntegration = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      return onDelete({ variables: { id: googleChatId } })
        .then(() => {
          toast.success<string>(
            t("client:client.integrationForm.toast.deleted"),
          );
        })
        .catch((error) => {
          toast.error<string>(
            error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
          );
        });
    },
    [googleChatId, onDelete, t],
  );

  const onBeforeRemove = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      setShow(true);
    },
    [setShow],
  );

  const apiUrl = getApiUrl();

  if (!hasGoogleChatIntegration) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-end align-items-center my-4">
          <a
            href={qs.stringifyUrl(
              {
                url: `${apiUrl}/api/v1/googlechat/auth/login`,
                query: {
                  client: clientId,
                  redirectUri: window.location.href,
                },
              },
              { encode: true },
            )}
            className="btn btn-primary"
          >
            {t("client:client.integrations.googleChat.button.generate")}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-end mb-3">
        <button onClick={onBeforeRemove} className="btn btn-danger">
          {t("client:client.integrations.googleChat.button.delete")}
        </button>
      </div>

      <Card className="mb-3">
        <Card.Header>
          {t("client:client.integrationForm.form.information")}
        </Card.Header>
        <Card.Body>
          {t("client:client.integrationForm.form.account")}
          {googleChatData?.googleChat?.googleAccountId ?? ""}
          <hr />
          {t("client:client.integrationForm.form.email")}
          {googleChatData?.googleChat?.email ?? ""}
          <hr />
          {t("client:client.integrationForm.form.name")}
          {googleChatData?.googleChat?.name ?? ""}
          <hr />
          {t("client:client.integrationForm.form.created")}
          {dayjs(googleChatData?.googleChat?.createdAt).format("L LT") ?? ""}
        </Card.Body>
      </Card>

      <Modal show={show} onHide={setShow} backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>
            {t("client:client.integrations.googleChat.modal.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("client:client.integrations.googleChat.modal.body")}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={setShow}>
            {t("common:cancel")}
          </Button>
          <Button variant="danger" onClick={onRemoveClientIntegration}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

const GoogleChatFeature: React.FC = () => {
  const { t } = useTranslation(["common"]);
  const clientId = useClientId();
  const isAdmin = useHasRole(["ADMIN"]);

  const { data } = useQuery(getClientFeatureQuery("googleChat"), {
    variables: { id: clientId },
    context: { headers: { ClientId: clientId } },
    fetchPolicy: "network-only",
  });

  const [toggleFeature] = useMutation(TOGGLE_CLIENT_FEATURE, {
    refetchQueries: [
      {
        query: getClientFeatureQuery("googleChat"),
        variables: { id: clientId },
      },
    ],
  });

  const googleChatEnabled =
    data?.client?.preferences?.googleChat?.enabled ?? false;

  const onToggleGoogleChat = useCallback(
    async (enabled: boolean) => {
      try {
        await toggleFeature({
          variables: {
            input: {
              id: clientId,
              feature: "googleChat",
              enabled,
            },
          },
        });
        toast.success<string>(
          t(`common:premiumFeature.${enabled ? "enabled" : "disabled"}`, {
            feature: "Google Chat",
          }),
        );
      } catch (e) {
        toast.error<string>(
          t(`common:premiumFeature.${enabled ? "enabled" : "disabled"}Error`, {
            feature: "Google Chat",
          }),
        );
      }
    },
    [clientId, toggleFeature, t],
  );

  if (!googleChatEnabled) {
    return (
      <div
        className="tab-pane fade show active"
        id="googleChat"
        role="tabpanel"
      >
        {isAdmin ? (
          <ToggleFeatureButton
            featureName="Google Chat"
            onClick={() => onToggleGoogleChat(true)}
          />
        ) : (
          <div className="text-muted ml-3">
            <h5 className="mb-3">{t("common:premiumFeature.title")}</h5>
            <p>{t("common:premiumFeature.text")}</p>
            <p>{t("common:premiumFeature.contact")}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="tab-pane fade show active" id="googleChat" role="tabpanel">
      <GoogleChatIntegration />
      {isAdmin && (
        <div className="mt-3 ml-3">
          <ToggleFeatureButton
            featureName="Google Chat"
            disable
            onClick={() => onToggleGoogleChat(false)}
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(GoogleChatFeature);
